/**
 * Catálogo de "Ajustes del bot" (bot de ventas · bandeja cotizaciones).
 *
 * Decisión del dueño (2026-10-08): el flujo es UNO solo, así que los switches
 * de flujo (registro, buró, expediente, cierre, web…) y los datos técnicos
 * (límites y modos) NO van en el panel. Aquí quedan solo los ajustes de
 * operación.
 *
 * Los switches siguen siendo vista previa: no hay backend para ellos todavía.
 * La forma es la que devolverá `GET /bot-ventas/variables`, para que el día
 * que exista solo cambie `useBotAjustes()`.
 *
 * Fuente de textos: backend/docs/plans/bot-ventas-v2-variables-dashboard-plan.md
 * (§8 valores, §9 textos reales de bot_ventas.respuestas).
 *
 * Sección:
 *   id, titulo, descripcion, nota?
 *   bloque?    'allowlist' | 'destinatario' · bloque que SÍ se guarda
 *   switches   Switch[]
 *
 * Switch:
 *   clave      env sin el prefijo BOT_VENTAS_
 *   titulo, queHace
 *   valor      boolean · lo que hoy tiene el servidor
 *   origen     '.env' | 'serverless' | 'default'
 *   editable   false = solo se muestra (ENABLED)
 *   nota?      aclaración corta bajo la descripción
 *   chat       { on: Burbuja[], off: Burbuja[] }
 *
 * Burbuja: { de: 'lead' | 'bot' | 'sistema', texto, etiqueta?, botones? }
 */

// ── Piezas de chat que se repiten ────────────────────────────────────────
const lead = (texto, extra) => ({ de: 'lead', texto, ...extra });
const bot = (texto, extra) => ({ de: 'bot', texto, ...extra });
const sis = (texto, extra) => ({ de: 'sistema', texto, ...extra });

const COTIZACION_QUEDO = 'Así quedó: Jetta 2025 a 48 meses, pago inicial de $95,000 y renta de $8,450 al mes.\nEs un estimado sujeto a revisión.';

const sw = (clave, datos) => ({ clave, origen: 'default', editable: true, ...datos });

export const SECCIONES = [
  {
    id: 'general',
    titulo: 'General',
    descripcion: 'Si el bot contesta y si escribe solo cuando el lead se queda callado.',
    switches: [
      sw('ENABLED', {
        soloPanel: true,
        titulo: 'Bot encendido',
        queHace: 'Interruptor maestro. Apagado, nadie le contesta al lead en automático.',
        valor: true,
        origen: '.env',
        editable: false,
        nota: 'Se prende y apaga con el switch de arriba de Conversaciones.',
        chat: {
          on: [lead('Hola, me interesa el Jetta 2025'), sis('Aria contesta en automático.')],
          off: [
            lead('Hola, me interesa el Jetta 2025'),
            sis('Nadie contesta en automático. El mensaje se guarda y hay que contestarlo a mano.'),
          ],
        },
      }),
      sw('RESCATE', {
        soloPanel: true,
        titulo: 'Recordatorio por silencio',
        queHace: 'Si el lead deja de contestar, Aria le escribe una vez entre 2 y 4 horas después, dentro de la ventana de 24 h.',
        valor: true,
        chat: {
          on: [
            sis('Pasan 3 horas sin respuesta del lead.'),
            bot('Ahí te dejo la cotización por si la quieres ver con calma. Si quieres le muevo al plazo o al enganche, nada más dime.'),
          ],
          off: [sis('Pasan 3 horas sin respuesta del lead.'), sis('No se le manda nada.')],
        },
      }),
    ],
  },
  {
    id: 'allowlist',
    titulo: '¿A quién le contesta el bot?',
    descripcion: 'Para hacer pruebas sin escribirles a leads reales.',
    bloque: 'allowlist',
    switches: [],
  },
  {
    id: 'asesor',
    titulo: 'Avisos al asesor',
    descripcion: 'Por dónde se entera el asesor de lo que pasa en el chat.',
    bloque: 'destinatario',
    switches: [
      sw('AVISO_WA', {
        titulo: 'Avisos al asesor por WhatsApp',
        queHace: 'Todos los avisos del bot al asesor salen también por WhatsApp.',
        valor: true,
        origen: '.env',
        chat: {
          on: [lead('Acepto la cotización', { etiqueta: 'botón' }), sis('Le llega una plantilla al WhatsApp del asesor.', { etiqueta: 'alerta_asesor_cotizacion_v2' })],
          off: [lead('Acepto la cotización', { etiqueta: 'botón' }), sis('No le llega WhatsApp al asesor: solo correo.')],
        },
      }),
      sw('AVISO_EMAIL', {
        titulo: 'Avisos al asesor por correo',
        queHace: 'Los avisos al asesor salen por correo.',
        valor: true,
        chat: {
          on: [lead('Acepto la cotización', { etiqueta: 'botón' }), sis('Le llega un correo al asesor.')],
          off: [
            lead('Acepto la cotización', { etiqueta: 'botón' }),
            sis('No le llega correo. Si los avisos por WhatsApp también están apagados, el asesor no se entera de nada.'),
          ],
        },
      }),
    ],
  },
  {
    id: 'ia',
    titulo: 'Inteligencia artificial',
    descripcion: 'Cómo entiende Aria lo que escribe el lead y cómo redacta lo que contesta.',
    nota: 'Si la IA se pone rara, apágalos aquí: el bot sigue contestando con textos fijos, sin desplegar nada.',
    switches: [
      sw('ROUTER_LLM', {
        titulo: 'Entender texto libre con IA',
        queHace: 'La IA clasifica lo que no entendieron los botones ni las frases fijas.',
        valor: true,
        origen: '.env',
        chat: {
          on: [
            lead('oye y si le doy más de entrada baja la mensualidad?'),
            sis('La IA entiende que quiere subir el enganche y recotiza.'),
          ],
          off: [
            lead('oye y si le doy más de entrada baja la mensualidad?'),
            sis('Solo entiende botones y frases conocidas. Después de 2 "no te entendí" pasa al asesor.'),
            bot('Perdón, creo que no te entendí bien. Mejor le paso tu mensaje a un compañero para que te ayude directo.'),
          ],
        },
      }),
      sw('REDACTOR_LLM', {
        titulo: 'Redactar con IA',
        queHace: 'La IA redacta sobre cifras ya calculadas: nunca inventa números.',
        valor: true,
        origen: '.env',
        chat: {
          on: [
            lead('¿Y a 48 meses?'),
            bot('¡Claro! A 48 meses tu Jetta 2025 queda en $8,450 al mes, con el mismo pago inicial de $95,000. ¿Te late así?', { etiqueta: 'lo redacta la IA' }),
          ],
          off: [lead('¿Y a 48 meses?'), bot(COTIZACION_QUEDO, { etiqueta: 'texto fijo' })],
        },
      }),
    ],
  },
  {
    id: 'extras',
    titulo: 'Extras',
    descripcion: 'Detalles que se pueden prender o apagar sin cambiar el flujo.',
    switches: [
      sw('AVISO_COTIZACION', {
        titulo: 'Avisar cotización actualizada',
        queHace: 'Si la cotización cambia fuera del chat (landing o admin), Aria lo dice en su siguiente mensaje. Es para el lead, no para el asesor.',
        valor: true,
        chat: {
          on: [
            sis('Un asesor cambió la cotización desde el admin.'),
            lead('Hola, sigo interesada'),
            bot(`Antes de seguir: tu cotización se actualizó. ${COTIZACION_QUEDO}`),
          ],
          off: [
            sis('Un asesor cambió la cotización desde el admin.'),
            lead('Hola, sigo interesada'),
            sis('Aria no lo menciona. El aviso solo se ve en el panel.'),
          ],
        },
      }),
      sw('OTRO_AUTO', {
        titulo: 'Cotizar otro auto',
        queHace: 'Cotiza desde cero otro auto, otro año u otra condición.',
        valor: true,
        origen: '.env',
        chat: {
          on: [lead('¿Y cuánto saldría un Tiguan 2024?'), sis('Arma una cotización nueva del Tiguan 2024.')],
          off: [lead('¿Y cuánto saldría un Tiguan 2024?'), sis('No cotiza otro auto: pasa al asesor.')],
        },
      }),
      sw('PDF_MANUAL_ENABLED', {
        titulo: 'Botón "Mandar PDF"',
        queHace: 'En Conversaciones, el asesor manda la cotización como PDF de WhatsApp.',
        valor: true,
        origen: '.env',
        chat: {
          on: [sis('En Conversaciones aparece el botón "Mandar PDF".'), bot('📄 Cotización PEICAR', { etiqueta: 'PDF por WhatsApp' })],
          off: [sis('No aparece el botón, y si alguien lo intenta, el servidor lo rechaza.')],
        },
      }),
    ],
  },
];

export const CATALOGO = { secciones: SECCIONES };

export default CATALOGO;
